// Current-workspace state for the PWA.
//
// M4 E1: adds the network API (listWorkspaces, createWorkspace) plus a small
// subscription surface so the header switcher can react to changes made in
// other tabs and to fresh sign-ins that haven't picked a workspace yet.
//
// The backend falls back to the caller's personal workspace when the
// X-Workspace-Id header is missing, so returning null from getCurrentWorkspaceId
// is always safe — the user just sees their personal vault until the switcher
// picks something.

import { CONFIG } from "../config";
import { idToken } from "./auth";

const STORAGE_KEY = "ordall.currentWorkspaceId";
const EVENT_NAME = "ordall:workspace-changed";

// ----------------------------------------------------------------------------
// Local storage helpers
// ----------------------------------------------------------------------------

// Returns the currently selected workspace id, or null if none has been set.
// Callers should treat null as "let the backend pick the default".
export function getCurrentWorkspaceId(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    // localStorage may be unavailable in private browsing / some embedded
    // WebViews — fall back to null and let the backend default kick in.
    return null;
  }
}

// Persist the caller's current workspace selection. Passing null clears it.
// Fires an in-page CustomEvent so subscribers (the switcher, evidence list)
// can react without waiting for a full navigation. Cross-tab changes still
// come through the native "storage" event.
export function setCurrentWorkspaceId(workspaceId: string | null): void {
  try {
    if (workspaceId && workspaceId.trim()) {
      window.localStorage.setItem(STORAGE_KEY, workspaceId.trim());
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { workspaceId } }));
  } catch {
    // Best-effort — if we can't persist, the header simply won't be sent and
    // the backend default is used. No user-visible failure.
  }
}

// Subscribe to workspace changes from any source (same-tab set, storage event
// from another tab). Returns an unsubscribe function.
export function subscribeWorkspaceChanges(cb: (workspaceId: string | null) => void): () => void {
  const onCustom = () => cb(getCurrentWorkspaceId());
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb(getCurrentWorkspaceId());
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

// ----------------------------------------------------------------------------
// Network API
// ----------------------------------------------------------------------------

export interface Workspace {
  workspaceId: string;
  name: string;
  kind: "personal" | "team";
  plan: "free" | "pro" | "business";
  role: "owner" | "admin" | "member" | "viewer";
  createdAt: string;
}

// Not routed through lib/api.ts because that module already imports from here
// (getCurrentWorkspaceId). Keeping this call out of the loop avoids the cycle
// and matches the semantics we want: /workspaces is workspace-agnostic — the
// header would be ignored anyway.
async function callWorkspacesApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");
  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${token}`);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const r = await fetch(`${CONFIG.api}${path}`, { ...init, headers });
  const text = await r.text();
  const data = text ? JSON.parse(text) : null;
  if (!r.ok) {
    const msg = (data && typeof data === "object" && "message" in data) ? String((data as { message: unknown }).message) : `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return data as T;
}

export function listWorkspaces(): Promise<{ workspaces: Workspace[] }> {
  return callWorkspacesApi<{ workspaces: Workspace[] }>("/workspaces");
}

export function createWorkspace(name: string, plan: "free" | "pro" | "business" = "free"): Promise<Workspace> {
  return callWorkspacesApi<Workspace>("/workspaces", {
    method: "POST",
    body: JSON.stringify({ name, plan }),
  });
}

// Bootstraps activeWorkspaceId when nothing is persisted yet (fresh sign-in
// or brand-new device). Picks the personal workspace when present; otherwise
// falls back to the first entry. Safe to call more than once — a no-op if the
// user already has a selection.
export async function ensureActiveWorkspace(): Promise<Workspace | null> {
  if (getCurrentWorkspaceId()) {
    // Best-effort: verify the persisted id still exists in the user's list.
    // If not (workspace deleted elsewhere), fall through to picking a new one.
    try {
      const { workspaces } = await listWorkspaces();
      const current = workspaces.find((w) => w.workspaceId === getCurrentWorkspaceId());
      if (current) return current;
    } catch {
      return null; // network hiccup — leave the stale id, backend fallback handles it
    }
  }
  try {
    const { workspaces } = await listWorkspaces();
    if (!workspaces.length) return null;
    const personal = workspaces.find((w) => w.kind === "personal");
    const pick = personal ?? workspaces[0];
    setCurrentWorkspaceId(pick.workspaceId);
    return pick;
  } catch {
    return null;
  }
}
